import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CalendarPlus, ClipboardList, MapPin, Pencil, X } from "lucide-react";
import { api, ApiError } from "../../api/client";

/** A booked appraisal meeting, as the API returns it (`meeting` on a review). */
export interface MeetingInfo {
  at: string;
  minutes: number;
  location: string | null;
  tz: string | null;
  note: string | null;
  booked_at: string | null;
  local_date: string | null;
  local_time: string | null;
  when: string;
}

const MEETING_STATUSES = ["manager_review_submitted", "meeting_pending", "employee_response_pending"];

export function isMeetingStage(status: string): boolean {
  return MEETING_STATUSES.includes(status);
}

export function calendarUrl(reviewId: number): string {
  return `/api/reviews/${reviewId}/meeting.ics`;
}

function nextWorkingDay(): string {
  const d = new Date();
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";
  } catch {
    return "Europe/London";
  }
}

type Invites = Record<string, "sent" | "paused" | "failed">;

/** Book (or move) the review meeting. Both people get an invite with a calendar file. */
export function BookMeetingDialog({
  reviewId,
  employeeName,
  meeting,
  onClose,
  onBooked,
}: {
  reviewId: number;
  employeeName: string;
  meeting: MeetingInfo | null;
  onClose: () => void;
  onBooked: (meeting: MeetingInfo, invites: Invites) => void;
}) {
  const [day, setDay] = useState(meeting?.local_date ?? nextWorkingDay());
  const [time, setTime] = useState(meeting?.local_time ?? "10:00");
  const [minutes, setMinutes] = useState(meeting?.minutes ?? 45);
  const [location, setLocation] = useState(meeting?.location ?? "");
  const [note, setNote] = useState(meeting?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setError("");
    const start = new Date(`${day}T${time}`);
    if (Number.isNaN(start.getTime())) { setError("Pick a date and a time."); return; }
    if (start.getTime() < Date.now()) { setError("Pick a time that hasn't passed yet."); return; }
    setSaving(true);
    try {
      const res = await api.post<{ meeting: MeetingInfo; invites: Invites }>(`/api/reviews/${reviewId}/meeting`, {
        starts_at: start.toISOString(),
        minutes,
        location: location.trim() || null,
        note: note.trim() || null,
        tz: browserZone(),
      });
      onBooked(res.meeting, res.invites ?? {});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't book the meeting. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-meeting-title"
        className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="book-meeting-title" className="text-base font-semibold text-gray-900">
              {meeting ? "Change the review meeting" : "Book the review meeting"}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              With {employeeName}. We'll email you both an invite to add to your calendars.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100">
            <X size={18} aria-hidden />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block text-xs">
            <span className="font-medium text-gray-700">Date</span>
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="input mt-1 w-full" />
          </label>
          <label className="block text-xs">
            <span className="font-medium text-gray-700">Start time</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="input mt-1 w-full" />
          </label>
        </div>
        <div className="mt-3">
          <span className="block text-xs font-medium text-gray-700">How long</span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {[30, 45, 60, 90].map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={minutes === m}
                onClick={() => setMinutes(m)}
                className={`min-h-[36px] rounded-full border px-3 text-xs font-semibold ${minutes === m ? "border-pme-blue bg-pme-blue text-white" : "border-gray-200 text-gray-600 hover:border-pme-blue/40"}`}
              >
                {m === 90 ? "1 hr 30" : m === 60 ? "1 hour" : `${m} min`}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">45 minutes is usually enough.</p>
        </div>
        <label className="mt-3 block text-xs">
          <span className="font-medium text-gray-700">Where</span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Meeting room 2, or a Teams link"
            maxLength={250}
            className="input mt-1 w-full"
          />
        </label>
        <label className="mt-3 block text-xs">
          <span className="font-medium text-gray-700">Note for {employeeName.split(" ")[0]} (optional)</span>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Have a look back at your self-assessment before we meet."
            className="input mt-1 w-full"
          />
        </label>

        {error && <p role="alert" className="mt-3 text-xs text-red-600">{error}</p>}
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-600">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="flex-1 rounded-lg bg-pme-blue px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? "Booking…" : meeting ? "Save and send update" : "Book and send invites"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What happens after the manager review: book the meeting, add it to your
 * calendar, open the meeting guide. For the manager (and HR).
 */
export default function MeetingPanel({
  reviewId,
  employeeName,
  status,
  meeting: initial,
  autoOpen = false,
  compact = false,
  inGuide = false,
  onChanged,
}: {
  reviewId: number;
  employeeName: string;
  status: string;
  meeting: MeetingInfo | null | undefined;
  autoOpen?: boolean;
  compact?: boolean;
  /** Shown inside the meeting guide itself: no link back to the guide. */
  inGuide?: boolean;
  onChanged?: (meeting: MeetingInfo) => void;
}) {
  const [meeting, setMeeting] = useState<MeetingInfo | null>(initial ?? null);
  const [open, setOpen] = useState(autoOpen && !initial);
  const [notice, setNotice] = useState("");

  useEffect(() => { setMeeting(initial ?? null); }, [initial]);

  if (!isMeetingStage(status)) return null;

  const booked = (m: MeetingInfo, invites: Invites) => {
    setMeeting(m);
    setOpen(false);
    const vals = Object.values(invites);
    if (vals.length > 0 && vals.every((v) => v === "sent")) {
      setNotice("Booked. Invites sent to you both.");
    } else if (vals.some((v) => v === "paused")) {
      setNotice("Booked. Email is paused at the moment, so no invite went out: use Add to my calendar, and let them know.");
    } else {
      setNotice("Booked, but an invite couldn't be sent. Use Add to my calendar, and let them know.");
    }
    onChanged?.(m);
  };

  const guide = `/review?review=${reviewId}&tab=meeting`;

  return (
    <div className={`rounded-xl border ${meeting ? "border-green-200 bg-green-50/60" : "border-pme-blue/30 bg-pme-sky/40"} ${compact ? "p-3" : "p-4"}`}>
      {meeting ? (
        <div className="flex items-start gap-2.5">
          <CalendarCheck size={18} className="mt-0.5 shrink-0 text-green-700" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Review meeting booked</p>
            <p className="text-sm text-gray-800">{meeting.when}</p>
            {meeting.location && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600">
                <MapPin size={12} aria-hidden /> {meeting.location}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2.5">
          <CalendarPlus size={18} className="mt-0.5 shrink-0 text-pme-blue" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-gray-900">Next: the review meeting</p>
            <p className="text-xs text-gray-600">
              Book a time to talk it through with {employeeName.split(" ")[0]}. In the meeting you agree the
              final rating, their objectives and how often you'll catch up.
            </p>
          </div>
        </div>
      )}

      {notice && <p className="mt-2 text-xs font-medium text-gray-700">{notice}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {meeting ? (
          <>
            {!inGuide && (
            <Link to={guide} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-pme-blue px-3 text-xs font-semibold text-white hover:bg-pme-blue/90">
              <ClipboardList size={14} aria-hidden /> Open the meeting guide
            </Link>
            )}
            <a href={calendarUrl(reviewId)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
              <CalendarPlus size={14} aria-hidden /> Add to my calendar
            </a>
            <button type="button" onClick={() => setOpen(true)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
              <Pencil size={14} aria-hidden /> Change
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setOpen(true)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-pme-blue px-3 text-xs font-semibold text-white hover:bg-pme-blue/90">
              <CalendarPlus size={14} aria-hidden /> Book the meeting
            </button>
            {!inGuide && (
            <Link to={guide} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
              <ClipboardList size={14} aria-hidden /> Preview the meeting guide
            </Link>
            )}
          </>
        )}
      </div>

      {open && (
        <BookMeetingDialog
          reviewId={reviewId}
          employeeName={employeeName}
          meeting={meeting}
          onClose={() => setOpen(false)}
          onBooked={booked}
        />
      )}
    </div>
  );
}

/** For the employee: when and where, and how to get ready. */
export function MyMeetingCard({ reviewId, meeting, managerName }: { reviewId: number; meeting: MeetingInfo; managerName?: string | null }) {
  return (
    <div className="rounded-xl border border-green-200 bg-green-50/60 p-4">
      <div className="flex items-start gap-2.5">
        <CalendarCheck size={18} className="mt-0.5 shrink-0 text-green-700" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Your review meeting{managerName ? ` with ${managerName}` : ""}</p>
          <p className="text-sm text-gray-800">{meeting.when}</p>
          {meeting.location && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600"><MapPin size={12} aria-hidden /> {meeting.location}</p>
          )}
          {meeting.note && <p className="mt-1.5 text-xs italic text-gray-700">"{meeting.note}"</p>}
          <p className="mt-2 text-xs font-semibold text-gray-700">To get ready</p>
          <ul className="mt-0.5 space-y-0.5 text-xs text-gray-600">
            <li>Read back your self-assessment.</li>
            <li>Think of one or two things you'd like to work on next year.</li>
            <li>Note anything you'd like to raise, or support you'd like.</li>
          </ul>
        </div>
      </div>
      <div className="mt-3">
        <a href={calendarUrl(reviewId)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
          <CalendarPlus size={14} aria-hidden /> Add to my calendar
        </a>
      </div>
    </div>
  );
}
